import { useQuery, useSuspenseQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  GetEntityDetailDocument,
  GetEntityDetailGeoItemsDocument,
  type GetEntityDetailGeoItemsQuery,
  type GetEntityDetailGeoItemsQueryVariables,
  type GetEntityDetailQuery,
  type GetEntityDetailQueryVariables,
} from "~/features/entities/gqls";
import { useTenant } from "~/features/tenant/context";

/** Read a single entity record (Data-tab fields) for the current tenant. */
export function useEntityDetail(id: string) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const detail = useSuspenseQuery<GetEntityDetailQuery, GetEntityDetailQueryVariables>(GetEntityDetailDocument, {
    variables: { tenantId, id },
  });
  const geoItems = useQuery<GetEntityDetailGeoItemsQuery, GetEntityDetailGeoItemsQueryVariables>(
    GetEntityDetailGeoItemsDocument,
    { variables: { tenantId, recordId: id } },
  );
  const response =
    geoItems.error || geoItems.dataState !== "complete"
      ? undefined
      : geoItems.data.cnutGetPublishedRecordGeoItemsByRecord;
  const validationErrorMessage = response?.__typename === "ValidationError" ? response.message : undefined;

  useEffect(() => {
    if (geoItems.error) {
      Sentry.captureException(geoItems.error);
    } else if (validationErrorMessage) {
      Sentry.captureException(new Error(validationErrorMessage));
    }
  }, [geoItems.error, validationErrorMessage]);

  const refetch = async () => {
    const result = await detail.refetch();
    await geoItems.refetch({ tenantId, recordId: id }).catch(() => undefined);
    return result;
  };

  return {
    ...detail,
    refetch,
    recordGeoItems: response?.__typename === "RecordGeoItemsByRecordOutput" ? response.recordGeoItems : [],
  };
}
